import { Check, Star } from 'lucide-react';

import type { DashboardNotificationItem } from '../../types';
import { cn } from '../../utils/cn';
import { formatNotificationTime, severityLabels, sourceLabels } from './notificationMeta';

interface NotificationRowProps {
    item: DashboardNotificationItem;
    onOpen: (item: DashboardNotificationItem) => void;
    onMarkRead: (item: DashboardNotificationItem) => void;
    onToggleStar: (item: DashboardNotificationItem) => void;
    // Starred list mixes groups, so it names the group of each row.
    showSource?: boolean;
    withDate?: boolean;
}

// Unread rows get a red dot and full colour; seen ones are dimmed, like an inbox.
export function NotificationRow({ item, onOpen, onMarkRead, onToggleStar, showSource = false, withDate = false }: NotificationRowProps) {
    const unread = !item.read;
    const changedPriority = item.defaultSeverity && item.defaultSeverity !== item.severity && item.source !== 'MANUAL';

    return (
        <div className={cn('group flex items-start gap-1 rounded-lg transition-colors hover:bg-ink-50', !unread && 'opacity-60 hover:opacity-100')}>
            <button
                type="button"
                role="menuitem"
                onClick={() => onOpen(item)}
                className="flex min-w-0 flex-1 cursor-pointer items-start gap-2.5 rounded-lg px-2 py-2 text-left focus:outline-none focus-visible:bg-academy-50"
            >
                <span className="flex h-5 w-2.5 shrink-0 items-center justify-center" aria-hidden="true">
                    {unread ? <span className="h-2 w-2 rounded-full bg-danger-500" /> : null}
                </span>
                <span className="min-w-0 flex-1">
                    <span className={cn('block text-sm leading-5 text-ink-900', unread ? 'font-semibold' : 'font-normal')}>
                        {unread ? <span className="sr-only">Chưa xem: </span> : null}
                        {item.title}
                    </span>
                    <span className="mt-0.5 block text-xs leading-5 text-ink-500">
                        {[showSource ? sourceLabels[item.source] : '', item.description].filter(Boolean).join(' · ')}
                    </span>
                    {changedPriority || item.expired ? (
                        <span className="mt-0.5 block text-xs text-ink-400">
                            {[
                                changedPriority ? `Đã đổi từ ${severityLabels[item.defaultSeverity!]}` : '',
                                item.expired ? 'Không còn trong danh sách hiện tại' : '',
                            ].filter(Boolean).join(' · ')}
                        </span>
                    ) : null}
                </span>
                <span className="shrink-0 pt-0.5 text-xs text-ink-400">{formatNotificationTime(item.createdAt, withDate)}</span>
            </button>
            <div className="flex shrink-0 items-center pt-1">
                <button
                    type="button"
                    aria-label={item.starred ? 'Bỏ sao' : 'Gắn sao'}
                    aria-pressed={Boolean(item.starred)}
                    onClick={() => onToggleStar(item)}
                    className={cn(
                        'rounded-md p-1.5 hover:bg-ink-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-academy-200',
                        item.starred ? 'text-warning-500' : 'text-ink-300 hover:text-ink-500',
                    )}
                >
                    <Star className={cn('h-4 w-4', item.starred && 'fill-current')} />
                </button>
                {unread ? (
                    <button
                        type="button"
                        aria-label={`Đánh dấu đã xem: ${item.title}`}
                        title="Đánh dấu đã xem"
                        onClick={() => onMarkRead(item)}
                        className="rounded-md p-1.5 text-ink-400 hover:bg-ink-100 hover:text-academy-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-academy-200"
                    >
                        <Check className="h-4 w-4" />
                    </button>
                ) : <span className="w-7" aria-hidden="true" />}
            </div>
        </div>
    );
}
